// analytics/hooks/useOutpatientsData.ts
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';  // Import useSelector instead of connect
import { 
  outpatientServices 
} from '../services/endpoints/outpatients';
import { OutPatientResponse, DiagnosisResponse, DiagnosisTableData, ActivityPatientsDistributionResponse, ChartData, DistributionResponse, AppointmentDepartmentResponse, NewPatientStats, IAppointmentTypeDataResponse, AnalyticsViewMode, UniquePatientsResponse, SourceState, InsurerAppointments } from '../types';
import { ReduxStore, IPatient, IDepartment } from '../../../../constants/types';
import { DemographicPatient, demographicsState, toDemographicPatients } from '../utils/demographics';
import { payerCategoryChartData } from '../utils/payerCategoryGrouping';
import { useDecryptedPatients } from './useDecryptedPatients';
import { transformAppointmentsData, transformUniquePatientData, convertAppointmentTypeToChartData, convertNewPatientStatsToChartData } from '../services/transformers';
import { updatePatientsFromId } from '../../../../redux/actions/patientsActions';
import { outpatientChartsForMode } from '../config/analyticsCharts';

interface IAppointmentByType {
  id: string;
  name: string;
  count: number;
}

export interface OutpatientsData {
  // SHER-583: rows, not ChartData. The ICD-11 table is the only consumer and it needs one
  // object per diagnosis, not a series plus three arrays that have to stay index-aligned.
  diagnosis: DiagnosisTableData;
  appointmentDistribution: DistributionResponse;
  totalPatients: ChartData,
  // Both of these are whatever their transformer returns, taken from the transformer rather
  // than restated. They were declared as the raw response types, which was never true — that
  // is what produced two standing tsc errors at the assignment site — and a restated type is
  // what drifts in the first place.
  newPatients: ReturnType<typeof convertNewPatientStatsToChartData>;
  // SHER-896: the personal data alone cannot answer "how old was this patient at the visit",
  // and `IPersonalData` carries no id to look a date up by, so the date travels beside it.
  patientsDetails: DemographicPatient[];
  appointmentsByType: ReturnType<typeof convertAppointmentTypeToChartData>;
  appointmentOutcomes: any;
  appointmentsByDoctor: OutPatientResponse;
  appointementStats: OutPatientResponse;
  appointmentTypeData: any
  activityDistribution: ActivityPatientsDistributionResponse['stats'];
  waitTime: OutPatientResponse;
  // SHER-900. `categoryKind` is present only when the card was grouped by payer category; its
  // absence is the per-insurer fallback. Narrowed here rather than added to the shared
  // `ChartData` every tab reads, because only this card has two vocabularies.
  appointmentsByInsurance: ChartData & { categoryKind?: 'payerCategory' };
  // SHER-899. Every insurer, unfiltered — which is what the name says. BOTH Top-5 tables read
  // this one field and each filters by its own `payerCategories`, so a third payer-category
  // table is a config row rather than a third identical source.
  insurers: InsurerAppointments[];
}

// What a response needs besides itself to become chart data: the department list, for naming
// the appointment-distribution series, and the decrypted patients, for the demographic panel.
interface SelectContext {
  departments: IDepartment[];
  patients: IPatient[];
}

// What a readiness check needs beyond the response. It extends `SelectContext` rather than
// widening it: no `select` reads `patientsSettled`, and adding it there would put a readiness
// signal in the deps of the projection memo, re-running every chart's `select` when the decrypt
// settles.
interface ReadyContext extends SelectContext {
  /** Whether the bulk patient decrypt has FINISHED — not whether it produced anything. */
  patientsSettled: boolean;
}

type OutpatientFetch = (
  uuidInvestigation: string,
  uuidDepartment: string,
  startDate: string,
  endDate: string,
  signal?: AbortSignal,
) => Promise<any>;

// The requests this tab can make. A request is named separately from the chart source it
// feeds because several sources legitimately share one: the diagnosis call answers two
// charts, the appointments call another two.
const requests = {
  diagnosis: outpatientServices.getDiagnosisWithAppointments,
  appointmentDistribution: outpatientServices.getAppointments,
  totalPatients: outpatientServices.getTotalPatients,
  uniquePatients: outpatientServices.getUniquePatients,
  newPatients: outpatientServices.getNewPatientsRegistration,
  activityDistribution: outpatientServices.getActivity,
  appointmentsByInsurance: outpatientServices.getAppointmentsByInsurance,
  appointmentOutcomes: outpatientServices.getAppointmentOutcomes,
  appointmentsByDoctor: outpatientServices.getAppointmentsByDoctor,
  appointementStats: outpatientServices.getAppointmentStats,
  appointmentTypeData: outpatientServices.getAppointmentType,
  waitTime: outpatientServices.getWaitTime,
} satisfies Record<string, OutpatientFetch>;

type RequestKey = keyof typeof requests;

// Parameterised by the chart source it belongs to, so `select` is checked against the field
// it fills rather than against `any`. Declaring the return as `any` is how the first version
// of this table was written, and it silently removed the only compile-time check the old
// assembly had: develop built one object literal against `OutpatientsData`, so a transformer
// returning the wrong shape failed there.
interface OutpatientSource<K extends keyof OutpatientsData = keyof OutpatientsData> {
  // Which request answers this chart source.
  request: RequestKey;
  // Whether this chart needs decrypted patients out of redux, which is what gates the bulk
  // decrypt. It sits on the source that actually reads them rather than on the request, so
  // it states a fact about the chart instead of about everything the response touches.
  needsPatients?: boolean;
  // How the response becomes this chart's data. Omitted where the chart reads the response
  // as it comes back, which is most of them.
  select?: (response: any, context: SelectContext) => OutpatientsData[K];
  // What state this chart is in once its response has arrived. Omitted where the response is
  // the chart's only input, which is most of them.
  //
  // It lives on the row rather than in the shared reducer because deciding it requires knowing
  // this endpoint's payload shape, and the row is the one place already typed for it. It
  // returns the whole `SourceState` rather than a boolean so the row also owns the choice
  // between "still waiting" and "failed", and the error that goes with it: a boolean forced the
  // shared loop to make that choice from a flag describing one specific input, which every row
  // would then have inherited whether or not it waits on that input.
  ready?: (response: any, context: ReadyContext) => SourceState;
}

// What one endpoint is currently worth. One entry per request, overwritten whole on every
// transition, so a state cannot be half-applied — an earlier version kept the response, the
// in-flight set and the error in three parallel maps, and a successful retry left the old
// error standing beside the new response.
type EndpointState =
  | { status: 'pending' }
  | { status: 'ok'; response: any }
  | { status: 'error'; error: Error };

// SHER-562. One row per chart `source` — the identifier the config and the tab already
// speak — naming the request behind it and what its answer means.
//
// Keyed by source, not by request. Keyed by request, a response feeding two charts had to
// list them in a `provides` array, that array had to be inverted into a source -> requests
// index, and every consumer then folded a list that never held more than one element in
// practice. Keying by source says the same thing directly, and the array, the index and both
// folds go away.
//
// Nothing outside this table is fetched, so the two requests that used to fire on every load
// and feed no chart at all (`patients`, `department`) are simply absent.
const sourceTable = {
  diagnosis: {
    request: 'diagnosis',
    // SHER-583: the Female/Male split is counted here, from the decrypted patients this context
    // already carries, because patient sex never reaches the API in clear. `needsPatients` makes
    // that dependency explicit in the decrypt gate — both this chart and patientsDetails are
    // DETAILED-tier today, so the decrypt already fires whenever the table is on screen, which
    // is exactly why the reliance is worth declaring rather than leaving to coincidence.
    needsPatients: true,
    select: (response, { patients }) => transformUniquePatientData(response, patients),
  },
  // SHER-896. Was `request: 'diagnosis'`, which fed the panel the ids aggregated by the
  // ICD-diagnosis endpoint -- so it described "outpatients carrying a coded diagnosis" while
  // the tile beside it described "outpatients", and the two could not be made to agree. The
  // unique-patients endpoint shares the tile's predicate on the server, so they now agree by
  // construction rather than by coincidence.
  patientsDetails: {
    request: 'uniquePatients',
    needsPatients: true,
    // Projects to {age, sex} here rather than handing the chart the decrypted record: the panel
    // reads two of `IPersonalData`'s eight fields, and parsing birthdates against appointment
    // dates is a boundary job, not a chart's. `response` is narrowed from the table's `any` at
    // the one place that knows this endpoint's shape.
    select: (response: UniquePatientsResponse, { patients }) =>
      toDemographicPatients(response?.props?.patients, response?.props?.firstAppointmentDates, patients),
    // The panel also waits on the redux bulk decrypt, which lands independently of this
    // response. See `demographicsState` for what each outcome means.
    ready: (response: UniquePatientsResponse, { patients, patientsSettled }) =>
      demographicsState(response?.props?.patients, patients, patientsSettled),
  },
  appointmentDistribution: {
    request: 'appointmentDistribution',
    select: (response) => response.chartData,
  },
  appointmentsByType: {
    request: 'appointmentDistribution',
    select: (response, { departments }) =>
      convertAppointmentTypeToChartData(
        response.stats
          ? Object.keys(response.stats).map((uuid) => ({
              id: uuid,
              name: departments.find((department) => department.uuid === uuid)?.name || 'no name',
              count: response.stats[uuid],
            }))
          : [],
      ),
  },
  totalPatients: {
    request: 'totalPatients',
    select: (response) => response.props || {},
  },
  newPatients: {
    request: 'newPatients',
    select: (response) => convertNewPatientStatsToChartData(response.stats),
  },
  activityDistribution: {
    request: 'activityDistribution',
    select: (response) => response.stats,
  },
  // SHER-900. Grouped by payer category where the tenant has classified any of its insurers,
  // and otherwise exactly the per-insurer chart it has always shown — see
  // `payerCategoryChartData` for why that fallback is the gate instead of a tenant flag.
  appointmentsByInsurance: {
    request: 'appointmentsByInsurance',
    select: (response) => payerCategoryChartData(response?.props, response?.insurers),
  },
  // Read off the same response the pie reads `props` off, so the two cannot describe different
  // periods. Which categories a table shows is a config concern, not a fetching one, so nothing
  // is filtered here.
  insurers: {
    request: 'appointmentsByInsurance',
    select: (response) => response.insurers ?? [],
  },
  appointmentOutcomes: { request: 'appointmentOutcomes' },
  appointmentsByDoctor: { request: 'appointmentsByDoctor' },
  appointementStats: { request: 'appointementStats' },
  appointmentTypeData: { request: 'appointmentTypeData' },
  waitTime: { request: 'waitTime' },
} satisfies { [K in keyof OutpatientsData]: OutpatientSource<K> };

// `satisfies` on both literals is what keeps their keys a union of the real names; annotating
// either instead would collapse it to `string`. The mapped type over `OutpatientsData` also
// makes the table total: a field added there with no row here does not compile.
// A Map rather than an object literal, so a config row whose `source` is named after
// something on Object.prototype cannot resolve to an inherited value.
const SOURCES = new Map<string, OutpatientSource>(Object.entries(sourceTable));

// A config row whose source has no row here needs nothing fetched. Today that is
// sexDistribution, averageWaitingTimes and pharmacyUsage — rows with no data behind them at
// all, and all of them visible: false.
//
// The Set collapses two chart sources backed by one request into a single fetch, and it has a
// live subject again: the insurance response feeds both the pie (`appointmentsByInsurance`) and
// the Top-5 tables (`insurers`). It lost the previous one when SHER-896 moved the demographics
// panel onto its own endpoint, and ran on fixtures alone in between — which is why the fixtures
// in useOutpatientsData.tier.sher562.test.tsx exist and should stay whatever the config does.
// Exported and taking the chart rows as an argument (SHER-896) so the deduplication can be
// tested on its own, against a fixture that shares a request, instead of against whichever
// pair the shipped config happens to contain.
export const dedupeRequests = (charts: { source: string }[]): RequestKey[] => {
  const keys = new Set<RequestKey>();
  charts.forEach((chart) => {
    const source = SOURCES.get(chart.source);
    if (source) keys.add(source.request);
  });
  return Array.from(keys);
};

const requestsForMode = (mode: AnalyticsViewMode): RequestKey[] =>
  dedupeRequests(outpatientChartsForMode(mode));

interface UseOutpatientsDataOptions {
  investigation: { uuid: string };
  departmentSelected?: string;
  startDate: string;
  endDate: string;
  mode: AnalyticsViewMode;
}

export const useOutpatientsData = ({
  investigation,
  departmentSelected,
  startDate,
  endDate,
  mode,
}: UseOutpatientsDataOptions) => {
  // One entry per endpoint, holding its whole status. Per endpoint rather than per tab
  // because a tab-wide error would let one rejected request blank out twelve working charts,
  // which is worse than the Promise.allSettled it replaced; and one entry rather than three
  // parallel maps because a transition has to land whole — split across maps, a successful
  // retry wrote the response without clearing the error and the card stayed on ErrorState.
  const [endpoints, setEndpoints] = useState<Partial<Record<RequestKey, EndpointState>>>({});

  // Get patients data from Redux store using useSelector
  const investigationsData = useSelector((state: ReduxStore) => {
    const investigations = state.hospital.data.departments;
    if (!investigations) return [];
    return investigations as IDepartment[];
  }, shallowEqual); // Add shallowEqual from react-redux


  const uuidDepartment = departmentSelected || 'all';
  // Everything cached is cached for one set of filters. When any of them moves, the
  // responses held for the previous filters are worthless and the cache resets.
  const filterKey = `${investigation?.uuid}|${uuidDepartment}|${startDate}|${endDate}`;

  // Reads the chart rows, not the requests: `patientsDetails` is the one source that touches
  // decrypted patients, and hanging the flag there rather than on the shared diagnosis request
  // means the decrypt is gated by whether a chart needs patients, not by whether some chart
  // happens to share a response with one that does.
  const needsPatients = useMemo(
    () => outpatientChartsForMode(mode).some((chart) => SOURCES.get(chart.source)?.needsPatients),
    [mode],
  );

  // SHER-854 made this decrypt non-blocking; SHER-562 stops it firing at all until a tier that
  // actually reads decrypted patients is on screen. SHER-897 moved the lifecycle itself out:
  // the same selector, state and effect were about to exist in a third hook.
  const { patients: patientsData, settled: patientsSettled } = useDecryptedPatients(
    investigation?.uuid,
    needsPatients,
  );

  const abortRef = useRef<AbortController | null>(null);
  // A mirror of `endpoints`, so the effect can read the current status synchronously — state
  // written earlier in the same pass has not rendered yet. It is a mirror, not a second
  // ledger: an earlier version kept a separate "already requested" Set, and the two could
  // disagree, which is the only reason a failed key needed unmarking by hand.
  const endpointsRef = useRef<Partial<Record<RequestKey, EndpointState>>>({});
  const filterKeyRef = useRef('');

  const commitEndpoints = (
    update: (
      current: Partial<Record<RequestKey, EndpointState>>,
    ) => Partial<Record<RequestKey, EndpointState>>,
  ) => {
    endpointsRef.current = update(endpointsRef.current);
    setEndpoints(endpointsRef.current);
  };

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!investigation?.uuid || !startDate || !endDate) {
      return;
    }

    if (filterKeyRef.current !== filterKey) {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      filterKeyRef.current = filterKey;
      commitEndpoints(() => ({}));
    }

    const controller = abortRef.current as AbortController;
    // Switching back to a tier already loaded for these filters costs nothing: a key with an
    // entry is not asked again until the filters move. An `error` entry is the exception —
    // it is precisely what "may be asked again" means, so re-entering the tier retries it.
    const toFetch = requestsForMode(mode).filter((key) => {
      const state = endpointsRef.current[key];
      return !state || state.status === 'error';
    });
    if (!toFetch.length) {
      return;
    }
    commitEndpoints((current) =>
      toFetch.reduce((next, key) => ({ ...next, [key]: { status: 'pending' } }), current),
    );

    // Fired independently rather than as one Promise.allSettled, so a slow endpoint holds
    // up its own card and nothing else.
    toFetch.forEach(async (key) => {
      try {
        const response = await requests[key](
          investigation.uuid,
          uuidDepartment,
          startDate,
          endDate,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        commitEndpoints((current) => ({ ...current, [key]: { status: 'ok', response } }));
      } catch (err) {
        if (!controller.signal.aborted) {
          commitEndpoints((current) => ({
            ...current,
            [key]: { status: 'error', error: err instanceof Error ? err : new Error('Unknown error') },
          }));
        }
      }
    });
  }, [filterKey, mode, investigation?.uuid, uuidDepartment, startDate, endDate]);

  // Built once and shared by both memos below. Constructing it twice is how the two drift:
  // a second hand-built context is a second dependency list to keep correct.
  const context = useMemo<SelectContext>(
    () => ({ departments: investigationsData, patients: patientsData }),
    [investigationsData, patientsData],
  );

  // Its own memo, so `data`'s dependencies stay free of the readiness signal.
  const readyContext = useMemo<ReadyContext>(
    () => ({ ...context, patientsSettled }),
    [context, patientsSettled],
  );

  const data = useMemo(() => {
    // Walks SOURCES rather than the literal, so every row is the widened `OutpatientSource`
    // and an absent `select` is a missing optional instead of a member the union lacks.
    return Array.from(SOURCES.entries()).reduce<Partial<OutpatientsData>>(
      (built, [source, { request, select }]) => {
        const entry = endpoints[request];
        // A 2xx with an empty body still lands as `ok`, and a `select` would dereference it.
        if (entry?.status !== 'ok' || !entry.response) return built;
        return {
          ...built,
          [source]: select ? select(entry.response, context) : entry.response,
        };
      },
      {},
    );
  }, [endpoints, context]);

  // Which chart sources are still waiting, and which came back rejected, so each card shows
  // its own placeholder or its own error instead of the whole tab doing either. Keyed by
  // chart `source`, so the tab never has to know which request backs a chart, and fixed in
  // precedence pending -> error -> ok. Scoped to the active tier, because an out-of-mode
  // source is legitimately never requested.
  const sourceStates = useMemo(() => {
    const sources = new Set(outpatientChartsForMode(mode).map((chart) => chart.source));

    return Array.from(sources).reduce<Record<string, SourceState>>((states, source) => {
      const entry = SOURCES.get(source);
      // A source no request backs folds to 'ok' with no data, and the chart draws its own
      // empty state — a skeleton there would spin forever.
      if (!entry) return { ...states, [source]: { status: 'ok' } };
      // A request with no entry yet has not been asked: the effect that marks them pending
      // runs after the first paint, and reading that frame as "loaded and empty" committed a
      // "No data" card on every mount and every tier switch.
      const state: EndpointState = endpoints[entry.request] ?? { status: 'pending' };
      if (state.status === 'pending') return { ...states, [source]: { status: 'pending' } };
      if (state.status === 'error') {
        return { ...states, [source]: { status: 'error', error: state.error } };
      }
      // SHER-896. A 2xx is not always enough: a source may have inputs that land separately
      // from its response, and resolving it on the response alone let the demographics card
      // announce "no outpatients in this period" while the patient decrypt was still in
      // flight. What "enough" means — and what failing to be enough means — is the row's
      // business, not this loop's. This loop only asks.
      if (entry.ready) {
        return { ...states, [source]: entry.ready(state.response, readyContext) };
      }
      return { ...states, [source]: { status: 'ok' } };
    }, {});
  }, [endpoints, mode, readyContext]);

  return { data, sourceStates };
};
